/*
  Team Development · Phase 3 components
  ----------------------------------------------------------------
    * PrivateNotes      — mentor-side notes section (owner-only
                          visibility, excluded from PDF/profile).
    * AuditTrailDialog  — module-scoped audit viewer for Owner/Manager.
    * MentorAssignmentDialog — grant/revoke mentors from the team list.
*/
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Lock, Pencil, Trash2, Save, PlusCircle, Loader2, History,
  UserPlus, UserMinus, Shield, Search,
} from "lucide-react";

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// ---------------------------------------------------------------------------
// PRIVATE NOTES
// ---------------------------------------------------------------------------
export function PrivateNotes({ staffId, scopeType = "profile", scopeId, me, canWrite }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingBody, setEditingBody] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ staff_id: staffId });
      if (scopeType) params.set("scope_type", scopeType);
      if (scopeId) params.set("scope_id", scopeId);
      const r = await api.get(`/team-development/private-notes?${params.toString()}`);
      setNotes(r.data.notes || []);
    } catch (e) {
      // 403 => just show empty, no toast noise.
      if (e?.response?.status !== 403) {
        toast.error("Couldn't load private notes", { description: e?.response?.data?.detail || e.message });
      }
      setNotes([]);
    } finally { setLoading(false); }
  }, [staffId, scopeType, scopeId]);
  useEffect(() => { load(); }, [load]);

  async function create() {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      await api.post("/team-development/private-notes", {
        staff_id: staffId, body: trimmed,
        scope_type: scopeType, scope_id: scopeId || staffId,
      });
      setBody("");
      toast.success("Private note added");
      load();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  async function saveEdit() {
    if (!editingBody.trim()) return;
    try {
      await api.patch(`/team-development/private-notes/${editingId}`, {
        body: editingBody.trim(),
      });
      toast.success("Note updated");
      setEditingId(null);
      setEditingBody("");
      load();
    } catch (e) {
      toast.error("Edit failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  async function remove(noteId) {
    if (!window.confirm("Delete this private note? (soft delete — audit trail retains it)")) return;
    try {
      await api.delete(`/team-development/private-notes/${noteId}`);
      toast.success("Note deleted");
      load();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  return (
    <Card
      className="p-3 rounded-md border-amber-200 bg-amber-50/40"
      data-testid={`td-private-notes-${scopeType}`}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-xs font-medium text-amber-900 flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5" />
          Private manager notes
          <Badge className="bg-amber-100 text-amber-900 border-amber-200 text-2xs ml-1">
            not shared · never exported
          </Badge>
        </div>
      </div>
      {canWrite && (
        <div className="mb-2 space-y-1">
          <Textarea
            rows={2}
            placeholder="Private reflections or coaching reminders — not visible to the staff member."
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="text-2xs bg-white"
            data-testid={`td-private-note-body-${scopeType}`}
          />
          <div className="flex justify-end">
            <Button
              size="sm" onClick={create}
              disabled={saving || !body.trim()}
              className="text-2xs h-7 bg-amber-700 hover:bg-amber-800 text-white"
              data-testid={`td-private-note-save-${scopeType}`}
            >
              {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <PlusCircle className="h-3 w-3 mr-1" />}
              Add note
            </Button>
          </div>
        </div>
      )}
      {loading && <div className="text-2xs text-zinc-500 italic">Loading…</div>}
      {!loading && notes.length === 0 && (
        <div className="text-2xs text-zinc-500 italic">
          No private notes yet{canWrite ? ". Add one above to keep coaching reflections off the shared record." : "."}
        </div>
      )}
      <div className="space-y-1.5">
        {notes.map((n) => {
          const isAuthor = me?.id && String(me.id) === String(n.author_id);
          const isEditing = editingId === n.id;
          return (
            <div
              key={n.id}
              className="rounded border border-amber-200 bg-white px-2 py-1.5"
              data-testid={`td-private-note-${n.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="text-2xs text-zinc-500">
                  <span className="font-medium text-zinc-700">{n.author_name || "someone"}</span>
                  <span> · {fmtDateTime(n.created_at)}</span>
                  {n.version > 1 && <span className="italic"> · edited</span>}
                </div>
                {isAuthor && !isEditing && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost" size="sm" className="h-5 px-1 text-2xs"
                      onClick={() => { setEditingId(n.id); setEditingBody(n.body); }}
                      data-testid={`td-private-note-edit-${n.id}`}
                    >
                      <Pencil className="h-2.5 w-2.5" />
                    </Button>
                    <Button
                      variant="ghost" size="sm" className="h-5 px-1 text-2xs text-red-700"
                      onClick={() => remove(n.id)}
                      data-testid={`td-private-note-delete-${n.id}`}
                    >
                      <Trash2 className="h-2.5 w-2.5" />
                    </Button>
                  </div>
                )}
              </div>
              {!isEditing && (
                <div className="whitespace-pre-wrap text-2xs text-zinc-800 mt-1">{n.body}</div>
              )}
              {isEditing && (
                <div className="mt-1 space-y-1">
                  <Textarea
                    rows={3}
                    value={editingBody}
                    onChange={(e) => setEditingBody(e.target.value)}
                    className="text-2xs bg-white"
                    data-testid={`td-private-note-edit-body-${n.id}`}
                  />
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="outline" className="text-2xs h-6"
                      onClick={() => { setEditingId(null); setEditingBody(""); }}>
                      Cancel
                    </Button>
                    <Button size="sm" className="text-2xs h-6 bg-amber-700 hover:bg-amber-800 text-white"
                      onClick={saveEdit}
                      data-testid={`td-private-note-save-edit-${n.id}`}>
                      <Save className="h-2.5 w-2.5 mr-1" />Save
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// AUDIT TRAIL
// ---------------------------------------------------------------------------
const ENTITY_LABELS = {
  mentor_assignment: "Mentor assignment",
  goal: "Goal",
  meeting: "Meeting",
  meeting_preparation: "Preparation",
  development_action: "Action",
  development_learning_link: "Learning link",
  private_note: "Private note",
};

export function AuditTrailDialog({ open, onClose, staffId, staffName }) {
  const [rows, setRows] = useState([]);
  const [actors, setActors] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (staffId) params.set("staff_id", staffId);
      if (filterType && filterType !== "all") params.set("entity_type", filterType);
      if (fromDate) params.set("from", `${fromDate}T00:00:00+00:00`);
      if (toDate) params.set("to", `${toDate}T23:59:59+00:00`);
      params.set("limit", "500");
      const r = await api.get(`/team-development/audit?${params.toString()}`);
      setRows(r.data.events || []);
      setActors(r.data.actors || {});
    } catch (e) {
      toast.error("Couldn't load audit log", { description: e?.response?.data?.detail || e.message });
      setRows([]);
    } finally { setLoading(false); }
  }, [staffId, filterType, fromDate, toDate]);

  useEffect(() => { if (open) load(); }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="td-audit-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-4 w-4" /> Audit trail
            {staffName && <span className="text-sm text-zinc-500 font-normal">· {staffName}</span>}
          </DialogTitle>
          <DialogDescription className="text-2xs">
            Read-only compliance log of every change in the Team Development module.
            Private notes appear here as metadata only — the body text is never surfaced.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2 mb-2">
          <div>
            <Label className="text-2xs">Entity</Label>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-7 text-2xs" data-testid="td-audit-filter-entity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All entities</SelectItem>
                {Object.entries(ENTITY_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-2xs">From</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="h-7 text-2xs" data-testid="td-audit-filter-from" />
          </div>
          <div>
            <Label className="text-2xs">To</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="h-7 text-2xs" data-testid="td-audit-filter-to" />
          </div>
        </div>

        {loading && (
          <div className="text-xs text-zinc-500 flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading events…
          </div>
        )}
        {!loading && rows.length === 0 && (
          <div className="text-xs text-zinc-500 italic">No events match these filters.</div>
        )}
        <div className="space-y-1" data-testid="td-audit-list">
          {rows.map((e) => (
            <div
              key={e.id}
              className="border border-zinc-200 rounded px-2 py-1.5 text-2xs"
              data-testid={`td-audit-row-${e.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div>
                  <Badge className="bg-indigo-50 text-indigo-800 border-indigo-200 mr-1 text-2xs">
                    {ENTITY_LABELS[e.entity_type] || e.entity_type}
                  </Badge>
                  <span className="font-medium text-zinc-800">{e.event_type}</span>
                  {e.version != null && <span className="text-zinc-500"> · v{e.version}</span>}
                  {e.reason && <span className="text-amber-700"> · {e.reason}</span>}
                </div>
                <div className="text-2xs text-zinc-500">{fmtDateTime(e.timestamp)}</div>
              </div>
              <div className="text-2xs text-zinc-500 mt-0.5">
                <span className="font-medium">{actors[e.actor_id] || e.actor_id || "system"}</span>
                <span> · entity {String(e.entity_id || "").slice(0, 8)}</span>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// MENTOR ASSIGNMENTS
// ---------------------------------------------------------------------------
export function MentorAssignmentDialog({ open, onClose, staff, candidates, onChange }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    if (!staff?.staff_id) return;
    setLoading(true);
    try {
      const r = await api.get(
        `/team-development/mentor-assignments?staff_id=${staff.staff_id}&active_only=true`,
      );
      setAssignments(r.data.assignments || []);
    } catch (e) {
      toast.error("Couldn't load mentors", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [staff?.staff_id]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const activeMentorIds = useMemo(() =>
    new Set(assignments.map((a) => String(a.mentor_id))), [assignments]);

  const available = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (candidates || [])
      .filter((c) => String(c.staff_id) !== String(staff?.staff_id))
      .filter((c) => !activeMentorIds.has(String(c.staff_id)))
      .filter((c) => !q
        || (c.name || "").toLowerCase().includes(q)
        || (c.email || "").toLowerCase().includes(q));
  }, [candidates, activeMentorIds, staff?.staff_id, search]);

  async function assign() {
    if (!picked) return toast.error("Pick a user to assign as mentor");
    setSaving(true);
    try {
      await api.post("/team-development/mentor-assignments", {
        staff_id: staff.staff_id, mentor_id: picked,
      });
      toast.success("Mentor assigned");
      setPicked("");
      load();
      onChange?.();
    } catch (e) {
      toast.error("Assign failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  }

  async function revoke(a) {
    const reason = window.prompt("Reason for ending this mentor assignment? (optional)") || "";
    try {
      await api.post(`/team-development/mentor-assignments/${a.id}/end`, { reason });
      toast.success("Mentor assignment ended");
      load();
      onChange?.();
    } catch (e) {
      toast.error("Revoke failed", { description: e?.response?.data?.detail || e.message });
    }
  }

  // Pre-compute an id → name map for current mentors (names arrive via team list).
  const nameById = useMemo(() => {
    const map = new Map();
    (candidates || []).forEach((c) => map.set(String(c.staff_id), c.name));
    return map;
  }, [candidates]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="td-mentor-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-4 w-4" /> Manage mentors
            {staff?.name && <span className="text-sm text-zinc-500 font-normal">· {staff.name}</span>}
          </DialogTitle>
          <DialogDescription className="text-2xs">
            Grant mentor rights so a teammate can create goals, run meetings and see private notes
            for this staff member. Revoking removes read access to prior private notes immediately.
          </DialogDescription>
        </DialogHeader>

        <div>
          <Label className="text-2xs">Current mentors</Label>
          {loading && <div className="text-2xs text-zinc-500">Loading…</div>}
          {!loading && assignments.length === 0 && (
            <div className="text-2xs text-zinc-500 italic">No active mentors yet.</div>
          )}
          <div className="space-y-1 mt-1">
            {assignments.map((a) => (
              <div
                key={a.id}
                className="flex items-center justify-between border border-zinc-200 rounded px-2 py-1 text-2xs"
                data-testid={`td-mentor-row-${a.mentor_id}`}
              >
                <div>
                  <div className="font-medium text-zinc-800">
                    {nameById.get(String(a.mentor_id)) || a.mentor_id.slice(-8)}
                  </div>
                  <div className="text-2xs text-zinc-500">Assigned {fmtDateTime(a.assigned_at)}</div>
                </div>
                <Button
                  variant="outline" size="sm" className="text-2xs h-6 text-red-700"
                  onClick={() => revoke(a)}
                  data-testid={`td-mentor-revoke-${a.mentor_id}`}
                >
                  <UserMinus className="h-2.5 w-2.5 mr-1" />Revoke
                </Button>
              </div>
            ))}
          </div>
        </div>

        <div className="pt-2 border-t border-zinc-100">
          <Label className="text-2xs">Grant mentor rights</Label>
          <div className="flex items-center gap-1 mb-1">
            <Search className="h-3 w-3 text-zinc-400" />
            <Input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by name or email…"
              className="h-7 text-2xs"
              data-testid="td-mentor-search"
            />
          </div>
          <Select value={picked} onValueChange={setPicked}>
            <SelectTrigger className="h-8 text-2xs" data-testid="td-mentor-picker">
              <SelectValue placeholder={available.length === 0 ? "No eligible users" : "Choose a teammate"} />
            </SelectTrigger>
            <SelectContent>
              {available.map((u) => (
                <SelectItem key={u.staff_id} value={u.staff_id}>
                  {u.name} <span className="text-zinc-400 text-2xs">· {u.role}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end mt-2">
            <Button
              onClick={assign} disabled={saving || !picked}
              className="text-xs h-7 bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="td-mentor-assign-save"
            >
              {saving && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
              <UserPlus className="h-3 w-3 mr-1" />Assign mentor
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Small reusable trigger button exported so pages can render it inline.
export function AuditTrailButton({ staff, dataTestId = "td-audit-open" }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        size="sm" variant="outline" className="text-2xs h-7"
        onClick={() => setOpen(true)} data-testid={dataTestId}
      >
        <History className="h-3 w-3 mr-1" /> Audit trail
      </Button>
      {open && (
        <AuditTrailDialog
          open={open}
          onClose={() => setOpen(false)}
          staffId={staff?.staff_id || staff?.id}
          staffName={staff?.name}
        />
      )}
    </>
  );
}
